import { AUTHORS, authorsMap } from '../document/sectionDocument.js'
import type { ColorPair } from './ychange.js'
import * as Y from 'yjs'

/**
 * Wie schreef welke zin: de auteurs van een sectiedocument en hun kleur (ontwerp 6.9).
 *
 * De map van client naar auteur wordt door de pod gevuld uit het token
 * (`apps/runtime/src/auteurs.ts`); deze module leest hem en geeft y-tiptap de twee dingen die
 * hij bij het renderen van een snapshot nodig heeft: de `Y.PermanentUserData` om per struct de
 * auteur op te zoeken, en een kleur per auteur zodat hij er zelf geen kiest.
 *
 * **Kleur is nooit de enige drager van informatie** (R-139). De naam van de auteur staat als
 * `data-auteur` bij de markering en de legenda noemt hem als tekst. Dat is ook de reden dat een
 * herhalende kleur bij veel auteurs kan: de kleur onderscheidt, de naam benoemt.
 */

/**
 * Het auteurspalet: zes kleuren, als verwijzingen naar de stylesheet en niet als hexwaarden.
 *
 * **Het designsysteem heeft geen auteurspalet**, en dat is geen omissie van deze module: het
 * feedbackrapport bij de handoff noemt "versiegeschiedenis en attributie, auteur-kleurlegenda"
 * zelf als een van de dingen die er nog niet zijn (`docs/bron/designsysteem/`). Wat hier staat is
 * daarom een besluit van de gebruiker op 3 september 2026, met de meting eronder; het designteam
 * kan het overrulen en dan verandert er één regel CSS per kleur en niets in dit bestand.
 *
 * **Waarom het er eerst vier waren, en waarom dat niet werkte.** De keuze was: neem de
 * merkkleuren die op zichzelf AA halen tegen wit. Dat leverde `primary`, `primary-deep`,
 * `primary-text` en `accent-purple`. Elk haalt wat hij moet halen, en juist daarom viel niet op
 * wat er mis was: de eerste drie zijn drie lichtheden van dezelfde teal, en `accent-purple` valt
 * met deuteranopie samen met `primary`. Onderling lagen ze 0,017 uit elkaar in OkLab, gemeten
 * over gewoon kleurzicht, deuteranopie en protanopie. Onder 0,05 zijn twee kleuren op een klein
 * vlak niet als verschillend te lezen. **Het palet onderscheidde dus niets.**
 *
 * **Wat het onderscheid maakt is lichtheid en niet kleurhoek.** Dat is gemeten: op de lichtheid
 * van de merkteal blijven en alleen aan de hoek draaien haalt 0,045 voor vier kleuren en 0,022
 * voor zes; meer verzadiging helpt nauwelijks (0,040 voor zes); lichtheid spreiden haalt 0,126.
 * De reden is eenvoudig: wie geen kleurzicht heeft, houdt lichtheid over.
 *
 * **De zes hieronder houden elk hun merkhoek en verschillen in lichtheid.** Ze halen samen
 * 0.089 (gewoon 0.098, deuteranopie 0.089,
 * protanopie 0.111). Elk haalt minstens 3:1 op wit, want de stip en het streepje
 * van de cursor zijn grafische objecten (WCAG 1.4.11), en de tint van elk draagt bodytekst boven
 * 6,7:1. De teal is de merkkleur zelf en de andere vijf zijn hun merkkleur naar zwart gemengd, dus
 * de hoek blijft van het merk en alleen de lichtheid is van ons. Zie `stijl/schil.css` voor de
 * waarden en `docs/dekking.md` bij M4.16 voor de hele afweging.
 *
 * **Waar kleur niets kan onderscheiden: de tint van de attributie.** Een wassing van 15 procent
 * is bijna wit, en zes van die wassingen liggen onderling 0,004 uit elkaar. Verhogen helpt niet:
 * bij 35 procent zijn ze 0,034 uit elkaar en valt de bodytekst met 4,42:1 onder AA. De tint zegt
 * dus dat een zin een auteur heeft, en niet welke; wie het wil weten leest de naam, en die staat
 * er altijd als tekst bij (R-139). Dat is geen tekortkoming van dit palet maar van 15 procent
 * dekking, en het geldt voor elk palet.
 */
export const AUTHOR_COLORS: readonly ColorPair[] = [1, 2, 3, 4, 5, 6].map((sectionNumber) => ({
  light: `var(--ef-auteur-${sectionNumber}-licht)`,
  dark: `var(--ef-auteur-${sectionNumber}-donker)`,
}))

/**
 * Welke kleur bij deze persoon hoort: deterministisch, zodat dezelfde mens overal dezelfde
 * kleur heeft, ook in een andere sectie en na herladen.
 *
 * Dezelfde vorm als in de CitatenPOC: een eenvoudige rolhash over de tekens. Het gaat om
 * spreiding en niet om onvoorspelbaarheid, dus een cryptografische hash zou hier alleen
 * asynchroon zijn zonder iets toe te voegen.
 */
export function colorFor(person: string): ColorPair {
  let hash = 0
  for (let i = 0; i < person.length; i += 1) {
    hash = (hash * 31 + person.charCodeAt(i)) >>> 0
  }
  const color = AUTHOR_COLORS[hash % AUTHOR_COLORS.length]
  if (color === undefined) throw new Error('het auteurspalet is leeg')
  return color
}

/** De auteurs die dit document kent, in een vaste orde. */
export function authorsIn(doc: Y.Doc): readonly string[] {
  return [...authorsMap(doc).keys()].sort((a, b) => a.localeCompare(b))
}

/**
 * De legenda: elke auteur met zijn kleur, voor het paneel naast de tekst.
 *
 * De naam is de persoonsaanduiding uit het token (`portaal:7f3a9c`) en niet een zelfgekozen
 * weergavenaam. Een leesbare naam hoort bij de deling en de gastheer, niet bij dit document;
 * de presence-balk van opdracht 12 gaat over dezelfde vraag.
 */
export function legend(doc: Y.Doc): readonly { persoon: string; kleur: ColorPair }[] {
  return authorsIn(doc).map((person) => ({ persoon: person, kleur: colorFor(person) }))
}

/**
 * De attributiecontext van één sectiedocument: precies wat `ySyncOptions` vraagt.
 *
 * `colorMapping` wordt vooraf gevuld voor elke bekende auteur. Zonder dat kiest y-tiptap zelf
 * een kleur uit zijn eigen palet, en dan heeft dezelfde mens in twee velden twee kleuren.
 */
export interface Attribution {
  readonly permanentUserData: Y.PermanentUserData
  readonly colorMapping: Map<string, ColorPair>
  /** Werkt de kleuren bij voor auteurs die er sinds de vorige keer bij zijn gekomen. */
  refreshColors(): void
}

/**
 * Eén attributiecontext per document, onthouden.
 *
 * **Niet per aanroep een nieuwe.** `Y.PermanentUserData` hangt bij het maken observers aan het
 * document en die zijn niet op te zeggen; per veld-editor een eigen instantie zou er dus per
 * veld een handler bij leggen die blijft. Bovendien moet dezelfde instantie naar elke editor
 * gaan én naar de snapshot-render, anders klopt de attributie niet.
 *
 * De cache hangt aan het document en niet aan een module-variabele met de documentnaam als
 * sleutel: zo verdwijnt hij met het document en houdt hij in een test geen vorige stand vast.
 */
const perDocument = new WeakMap<Y.Doc, Attribution>()

export function attributionOf(doc: Y.Doc): Attribution {
  const existing = perDocument.get(doc)
  if (existing !== undefined) return existing

  const permanentUserData = new Y.PermanentUserData(
    doc,
    authorsMap(doc) as unknown as Y.Map<unknown>,
  )
  const colorMapping = new Map<string, ColorPair>()
  const attribution: Attribution = {
    permanentUserData,
    colorMapping,
    refreshColors: () => {
      for (const person of authorsIn(doc)) {
        if (!colorMapping.has(person)) colorMapping.set(person, colorFor(person))
      }
    },
  }
  attribution.refreshColors()
  // De map groeit terwijl er mensen bij komen, en de wortelnaam is de enige plek waar dat aan
  // te zien is. Observeren en niet pollen: een nieuwe auteur hoort meteen zijn eigen kleur te
  // hebben, ook als er op dat moment geen snapshot wordt gerenderd.
  doc.getMap(AUTHORS).observe(() => attribution.refreshColors())

  perDocument.set(doc, attribution)
  return attribution
}
